/**
 * ExcessAlarmsTable — Section 1: alarms exceeding the response time threshold.
 * Includes search, sort, pagination, and configurable threshold input.
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { convertIsoToLocal } from '../../../../libs.jsx';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';
import NotesCell from '../../../../components/v2/NotesCell.jsx';
import CallsCell from '../../../../components/v2/CallsCell.jsx';

// ---------------------------------------------------------------------------
// RowActions (kebab menu — matches AlertDetailsTable)
// ---------------------------------------------------------------------------

const RowActions = ({ row, onEditReason, isReasonEditable }) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState({});
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    const handleToggle = (e) => {
        e.stopPropagation();
        if (!open && menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 180) {
                setMenuStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, right: `${window.innerWidth - rect.right}px`, top: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            } else {
                setMenuStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, right: `${window.innerWidth - rect.right}px`, bottom: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            }
        }
        setOpen(!open);
    };

    return (
        <div className="ap-row-actions" ref={menuRef}>
            <button type="button" className="ap-row-actions__trigger" onClick={handleToggle} aria-label="Actions" aria-expanded={open}>
                <i className="fe fe-more-vertical" aria-hidden="true" />
            </button>
            {open ? (
                <div className="ap-row-actions__menu" style={menuStyle}>
                    <Link to={`/profiles/event/${row.event_id}`} className="ap-row-actions__item" style={{ textDecoration: 'none' }} onClick={() => setOpen(false)}>
                        <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        View Details
                    </Link>
                    {isReasonEditable ? (
                        <button type="button" className="ap-row-actions__item" onClick={(e) => { e.stopPropagation(); setOpen(false); onEditReason(row); }}>
                            <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                            </svg>
                            Edit Reason
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

const COLUMNS = [
    { key: 'notes', label: 'Notes', sortable: false },
    // Calls column is only surfaced for Grandstream campuses (gated by isGrandstream).
    { key: 'calls', label: 'Calls', sortable: false },
    { key: 'event_datetime_local', label: 'Event Start', sortable: true },
    { key: 'event_type_name', label: 'Category/Type', sortable: true },
    { key: 'ack_datetime_local', label: 'Cleared', sortable: true },
    { key: 'area_name', label: 'Area', sortable: true },
    { key: 'resident_name', label: 'Resident', sortable: true },
    { key: 'description', label: 'Device', sortable: true },
    { key: 'display_name', label: 'Accepted By', sortable: true, filterable: true },
    { key: 'response_time', label: 'Response (min)', sortable: true },
    { key: 'reason_code_description', label: 'Reason', sortable: true }
];


// ---------------------------------------------------------------------------
// Reason cell — auto-close sentinels (-1, -2) get a dotted underline + popover
// ---------------------------------------------------------------------------
const AUTO_CLOSE_REASON_MESSAGES = {
    '-1': 'This event was automatically closed by the system due to autoclear timeout.',
    '-2': 'This event was automatically closed by the system due to auto finish timeout.',
};

const ReasonCell = ({ reasonId, description, edited }) => {
    const ref = useRef(null);
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState({});
    const message = AUTO_CLOSE_REASON_MESSAGES[String(reasonId)];
    const text = description || '--';

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    const editedBadge = edited ? (
        <span className="ex-reason-edited__badge" title="Reason was edited">{'\u270E'}</span>
    ) : null;
    const wrapperClass = edited ? 'ex-reason-edited' : '';

    if (!message) {
        return (
            <span className={wrapperClass}>
                {text}
                {editedBadge}
            </span>
        );
    }

    const handleEnter = () => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const popoverW = 260;
        const spaceBelow = window.innerHeight - rect.bottom;
        const centerX = rect.left + rect.width / 2;
        let left = centerX - popoverW / 2;
        left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));
        setStyle({
            position: 'fixed',
            zIndex: 9999,
            left: `${left}px`,
            top: spaceBelow < 100 ? undefined : `${rect.bottom + 6}px`,
            bottom: spaceBelow < 100 ? `${window.innerHeight - rect.top + 6}px` : undefined,
            width: `${popoverW}px`,
            whiteSpace: 'normal',
        });
        setOpen(true);
    };

    return (
        <span className={wrapperClass}>
            <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)} style={{ position: 'relative', display: 'inline-block' }}>
                <span className="dp-cell-text has-popover">{text}</span>
                {open ? (
                    <span className="na-type-popover" style={style}>
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{message}</span>
                    </span>
                ) : null}
            </span>
            {editedBadge}
        </span>
    );
};

function renderCell(row, colKey) {
    const val = row[colKey];
    switch (colKey) {
        case 'event_datetime_local':
            return row.event_id ? (
                <Link to={`/profiles/event/${row.event_id}`} className="ar-link">
                    {convertIsoToLocal(val) || val || '--'}
                </Link>
            ) : (convertIsoToLocal(val) || val || '--');
        case 'event_type_name':
            return (
                <div className="ex-stack-cell">
                    <span>{row.event_category_name || '--'}</span>
                    <span className="ex-stack-cell__sub">{val || ''}</span>
                </div>
            );
        case 'ack_datetime_local':
            return val ? (convertIsoToLocal(val) || val) : '--';
        case 'area_name':
            return row.area_id ? (
                <Link to={`/profiles/area/${row.area_id}`} className="ar-link">{val || '--'}</Link>
            ) : (val || '--');
        case 'resident_name':
            return row.resident_id ? (
                <Link to={`/profiles/resident/${row.resident_id}`} className="ar-link">{val || '--'}</Link>
            ) : (val || '--');
        case 'description':
            return (
                <div className="ex-stack-cell">
                    {row.device_id ? (
                        <Link to={`/profiles/inovonics/${row.device_id}`} className="ar-link">{val || '--'}</Link>
                    ) : (<span>{val || '--'}</span>)}
                    {row.manufacture_uuid ? (
                        <span className="ex-stack-cell__sub">{row.manufacture_uuid}</span>
                    ) : null}
                </div>
            );
        case 'response_time':
            return val != null ? Number(val).toLocaleString(undefined, { maximumFractionDigits: 1 }) : '--';
        case 'reason_code_description': {
            const hasEdits = row.reason_edits && parseInt(row.reason_edits, 10) > 0;
            return (
                <ReasonCell
                    reasonId={row.reason_id}
                    description={val}
                    edited={hasEdits}
                />
            );
        }
        case 'display_name':
            return (
                <div>
                    <span className="dp-cell-text">{row.display_name || '\u2014'}</span>
                    <br />
                    <span className="dp-cell-text dp-cell-text--muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                        {row.accept_datetime_local ? convertIsoToLocal(row.accept_datetime_local) : ''}
                    </span>
                </div>
            );
        default:
            return val != null ? String(val) : '--';
    }
}

const ExcessAlarmsTable = ({ data, execTime, onExecTimeChange, onSaveThreshold, savingThreshold, tzOffset = 0, onAddNote, onEditReason, eventNotesActive = false, isReasonEditable = false, isGrandstream = false }) => {
    const showKebab = eventNotesActive || isReasonEditable || true; // always show for View Details
    // The Calls column only exists for Grandstream campuses — hide it otherwise.
    const visibleColumns = useMemo(
        () => COLUMNS.filter((c) => c.key !== 'calls' || isGrandstream),
        [isGrandstream]
    );
    const [search, setSearch] = useState('');
    const [sortColumn, setSortColumn] = useState('response_time');
    const [sortOrder, setSortOrder] = useState('desc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();

    const searched = useMemo(() => {
        if (!search) return data;
        const term = search.toLowerCase();
        return data.filter((r) =>
            (r.resident_name || '').toLowerCase().includes(term) ||
            (r.area_name || '').toLowerCase().includes(term) ||
            (r.description || '').toLowerCase().includes(term) ||
            (r.reason_code_description || '').toLowerCase().includes(term)
        );
    }, [data, search]);

    const sorted = useMemo(() => {
        if (!sortColumn) return searched;
        return [...searched].sort((a, b) => {
            let aV = a[sortColumn], bV = b[sortColumn];
            if (aV == null) aV = '';
            if (bV == null) bV = '';
            if (sortColumn === 'response_time') {
                aV = parseFloat(aV) || 0; bV = parseFloat(bV) || 0;
                return sortOrder === 'asc' ? aV - bV : bV - aV;
            }
            const cmp = String(aV).localeCompare(String(bV), undefined, { numeric: true });
            return sortOrder === 'asc' ? cmp : -cmp;
        });
    }, [searched, sortColumn, sortOrder]);

    const total = sorted.length;
    const totalPages = Math.max(1, Math.ceil(total / perPage));
    const safePage = Math.min(page, totalPages);
    const paginated = useMemo(() => sorted.slice((safePage - 1) * perPage, safePage * perPage), [sorted, safePage, perPage]);
    const showStart = total === 0 ? 0 : (safePage - 1) * perPage + 1;
    const showEnd = Math.min(safePage * perPage, total);

    const pageNumbers = useMemo(() => {
        const pages = [];
        const max = 5;
        let s = Math.max(1, safePage - Math.floor(max / 2));
        let e = Math.min(totalPages, s + max - 1);
        if (e - s < max - 1) s = Math.max(1, e - max + 1);
        for (let i = s; i <= e; i++) pages.push(i);
        return pages;
    }, [safePage, totalPages]);

    const handleSort = useCallback((key) => {
        setSortColumn((prev) => {
            if (prev === key) { setSortOrder((o) => o === 'asc' ? 'desc' : 'asc'); return prev; }
            setSortOrder('asc'); return key;
        });
        setPage(1);
    }, []);

    return (
        <div className="ex-section">
            <div className="ex-section__header">
                <div className="ex-threshold-row">
                    <span className="ex-threshold-label">Alarms exceeding</span>
                    <input
                        type="number"
                        className="ex-threshold-input"
                        value={execTime}
                        min={1}
                        max={240}
                        onChange={(e) => onExecTimeChange(parseInt(e.target.value, 10) || 20)}
                    />
                    <span className="ex-threshold-unit">minute response time</span>
                    <button className="rp-btn rp-btn--secondary" onClick={onSaveThreshold} disabled={savingThreshold} style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}>
                        {savingThreshold ? 'Saving...' : 'Update'}
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="ap-controls" style={{ marginBottom: 'var(--space-3)' }}>
                <div className="ap-controls__left">
                    <div className="ap-search">
                        <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <input type="search" placeholder="Search excess alarms..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="ap-search__input" />
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="ap-table-container">
                <div className="dp-table-wrap">
                    <table className="dp-table">
                        <thead className="dp-thead">
                            <tr>
                                {showKebab ? (
                                    <th scope="col" className="ap-th ap-th--actions">Edit</th>
                                ) : null}
                                {visibleColumns.map((col) => (
                                    <th key={col.key} scope="col" className="ap-th">
                                        <div className="ap-th__content">
                                            {col.sortable ? (
                                                <button type="button" onClick={() => handleSort(col.key)} className="ap-th__sort-btn">
                                                    {col.label}
                                                    {sortColumn === col.key ? (
                                                        <svg className="ap-icon-sm" style={{ marginLeft: '2px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                                            {sortOrder === 'asc'
                                                                ? <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                                                : <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />}
                                                        </svg>
                                                    ) : null}
                                                </button>
                                            ) : (<span>{col.label}</span>)}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.length === 0 ? (
                                <tr className="dp-tr">
                                    <td className="dp-td" colSpan={visibleColumns.length + (showKebab ? 1 : 0)} style={{ textAlign: 'center', padding: 'var(--space-6)' }}>
                                        <span style={{ color: 'var(--color-text-muted)' }}>No excess alarms for this period.</span>
                                    </td>
                                </tr>
                            ) : null}
                            {paginated.map((row, idx) => (
                                <tr key={row.event_id || idx} className="dp-tr">
                                    {showKebab ? (
                                        <td className="dp-td" style={{ textAlign: 'center' }}>
                                            <RowActions
                                                row={row}
                                                onEditReason={onEditReason}
                                                isReasonEditable={isReasonEditable}
                                            />
                                        </td>
                                    ) : null}
                                    {visibleColumns.map((col) => (
                                        <td key={col.key} className="dp-td">
                                            {col.key === 'notes' ? (
                                                <NotesCell
                                                    row={row}
                                                    notes={row.notes}
                                                    onClick={onAddNote}
                                                    eventNotesActive={eventNotesActive}
                                                    tzOffset={tzOffset}
                                                    ariaPrefix={`Event ${row.event_id || ''}`}
                                                />
                                            ) : col.key === 'calls' ? (
                                                <CallsCell
                                                    calls={row.calls}
                                                    tzOffset={tzOffset}
                                                    ariaPrefix={`Event ${row.event_id || ''}`}
                                                />
                                            ) : renderCell(row, col.key)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {total > 0 ? (
                <div className="ap-pagination">
                    <p className="ap-pagination__info">
                        Showing <span className="ap-pagination__emphasis">{showStart}</span>&ndash;<span className="ap-pagination__emphasis">{showEnd}</span> of{' '}
                        <span className="ap-pagination__emphasis">{total}</span> excess alarms
                    </p>
                    <div className="ap-pagination__controls">
                        <div className="ap-per-page">
                            <label htmlFor="ex-per-page" className="ap-per-page__label">Rows per page:</label>
                            <select id="ex-per-page" value={perPage} onChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(1); }} className="ap-per-page__select">
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={200}>200</option>
                                <option value={300}>300</option>
                            </select>
                        </div>
                        <nav className="ap-page-nav" aria-label="Excess alarms pagination">
                            <button type="button" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage <= 1} className="ap-page-btn" aria-label="Previous">
                                <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                            </button>
                            {pageNumbers.map((n) => (
                                <button key={n} type="button" onClick={() => setPage(n)} className={`ap-page-num${n === safePage ? ' is-active' : ''}`}>{n}</button>
                            ))}
                            <button type="button" onClick={() => setPage(Math.min(totalPages, safePage + 1))} disabled={safePage >= totalPages} className="ap-page-btn" aria-label="Next">
                                <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                            </button>
                        </nav>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ExcessAlarmsTable;
